import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, DollarSign, Activity, ArrowDownRight } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../../services/api';

interface CrecheResumo {
  id: number;
  status: 'pendente' | 'ativa' | 'suspensa' | 'negada';
  valor_mensal?: number;
  plano?: string;
}

interface UserResumo {
  id: number;
}

export default function VisaoGeralPage() {
  const [creches, setCreches] = useState<CrecheResumo[]>([]);
  const [users, setUsers] = useState<UserResumo[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const carregarDados = async () => {
      try {
        const [rCreches, rUsers] = await Promise.allSettled([
          api.get<CrecheResumo[]>('/creches'),
          api.get<UserResumo[]>('/saas/usuarios'),
        ]);

        setCreches(rCreches.status === 'fulfilled' ? rCreches.value.data ?? [] : []);
        setUsers(rUsers.status === 'fulfilled' ? rUsers.value.data ?? [] : []);
      } finally {
        setCarregando(false);
      }
    };

    carregarDados();
  }, []);

  // Cálculos de KPI
  const ativas = creches.filter((c) => c.status === 'ativa').length;
  const pendentes = creches.filter((c) => c.status === 'pendente').length;
  const suspensas = creches.filter((c) => c.status === 'suspensa').length;
  const negadas = creches.filter((c) => c.status === 'negada').length;

  // MRR soma o que está efetivamente contratado. Não se adivinha valor: o
  // palpite anterior era `plano === 'Profissional' ? 397 : 197`, e como
  // 'Profissional' nunca existiu como plano de assinatura (são BRONZE, PRATA e
  // OURO), toda creche sem valor entrava como R$197 — uma creche no OURO
  // aparecia valendo um terço do que vale. Faturamento inventado é pior que
  // faturamento ausente.
  const mrr = creches
    .filter((c) => c.status === 'ativa')
    .reduce((acc, curr) => acc + (curr.valor_mensal || 0), 0);

  const totalCreches = creches.length;
  const totalUsuarios = users.length;

  // Histórico de MRR ainda não é rastreado — exibe apenas o mês atual (real)
  const mrrData = [
    { mes: 'Atual', mrr },
  ];

  const statusData = [
    { name: 'Ativas', value: ativas, color: '#10b981' },
    { name: 'Pendentes', value: pendentes, color: '#f59e0b' },
    { name: 'Suspensas', value: suspensas, color: '#ef4444' },
    { name: 'Negadas', value: negadas, color: '#6b7280' },
  ];

  const conversionRate = totalCreches > 0 ? ((ativas / totalCreches) * 100).toFixed(1) : '0';

  if (carregando) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-slate-400">Carregando dados do dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* MRR Card */}
        <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/30 rounded-2xl p-6 hover:border-blue-400/50 transition-all">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-slate-400 text-sm font-medium mb-1">MRR Estimado</p>
              <h3 className="text-3xl font-bold text-white">
                R$ {(mrr / 1000).toFixed(1)}k
              </h3>
              <p className="text-xs text-slate-500 mt-2">Receita Recorrente Mensal</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="text-blue-400" size={24} />
            </div>
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <span className="font-semibold">Sem histórico comparativo ainda</span>
          </div>
        </div>

        {/* Active Creches Card */}
        <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/30 rounded-2xl p-6 hover:border-green-400/50 transition-all">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-slate-400 text-sm font-medium mb-1">Creches Ativas</p>
              <h3 className="text-3xl font-bold text-white">{ativas}</h3>
              <p className="text-xs text-slate-500 mt-2">Gerando receita</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Activity className="text-green-400" size={24} />
            </div>
          </div>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <span>De {totalCreches} total</span>
          </div>
        </div>

        {/* Total Users Card */}
        <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/30 rounded-2xl p-6 hover:border-purple-400/50 transition-all">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-slate-400 text-sm font-medium mb-1">Usuários Ativos</p>
              <h3 className="text-3xl font-bold text-white">{totalUsuarios}</h3>
              <p className="text-xs text-slate-500 mt-2">Usando a plataforma</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Users className="text-purple-400" size={24} />
            </div>
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <span className="font-semibold">Sem histórico comparativo ainda</span>
          </div>
        </div>

        {/* Conversion Rate Card */}
        <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/30 rounded-2xl p-6 hover:border-orange-400/50 transition-all">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-slate-400 text-sm font-medium mb-1">Taxa de Conversão</p>
              <h3 className="text-3xl font-bold text-white">{conversionRate}%</h3>
              <p className="text-xs text-slate-500 mt-2">Leads convertidos</p>
            </div>
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-orange-400" size={24} />
            </div>
          </div>
          <div className={`flex items-center gap-2 text-sm ${pendentes > 0 ? 'text-orange-400' : 'text-green-400'}`}>
            {pendentes > 0 && <ArrowDownRight size={16} />}
            <span className="font-semibold">{pendentes} solicitações pendentes</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* MRR Chart */}
        <div className="lg:col-span-2 bg-gradient-to-br from-slate-800/50 to-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-6">Evolução do MRR</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mrrData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="mes" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '8px',
                }}
                labelStyle={{ color: '#e2e8f0' }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="mrr"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ fill: '#3b82f6', r: 5 }}
                activeDot={{ r: 7 }}
                name="MRR (R$)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Status Chart */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-6">Creches por Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '8px',
                }}
                labelStyle={{ color: '#e2e8f0' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Health Card */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">🏥 Saúde do Sistema</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Taxa de Uptime</span>
              <span className="font-bold text-slate-500">—</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Latência Média</span>
              <span className="font-bold text-slate-500">—</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Erros (24h)</span>
              <span className="font-bold text-slate-500">0</span>
            </div>
            <p className="text-xs text-slate-500 mt-4">Monitoramento ainda não implementado</p>
          </div>
        </div>

        {/* Next Steps Card */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">📋 Próximos Passos</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="text-blue-400 font-bold mt-0.5">✓</span>
              <span className="text-slate-300">Onboard {pendentes} creches em processamento</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 font-bold mt-0.5">✓</span>
              <span className="text-slate-300">Revisar {suspensas} creche(s) suspensa(s)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 font-bold mt-0.5">✓</span>
              <span className="text-slate-300">Otimizar painel de controle</span>
            </li>
          </ul>
        </div>

        {/* Statistics Card */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">📊 Estatísticas</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Total de Creches</span>
              <span className="font-bold text-white text-lg">{totalCreches}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Usuários Ativos</span>
              <span className="font-bold text-white text-lg">{totalUsuarios}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Receita Mensal</span>
              <span className="font-bold text-green-400 text-lg">
                R$ {(mrr / 1000).toFixed(1)}k
              </span>
            </div>
            <div className="pt-3 border-t border-slate-700/50 mt-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">Crescimento Mês</span>
                <span className="font-bold text-slate-500">Sem histórico ainda</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
